/**
 * Chunk-load recovery (Wave 1 — refresh-bug P0).
 *
 * Problem: deploys ship a fresh `frontend/dist` via `rsync --delete`, which
 * purges the previous build's content-hashed chunks. A browser tab that is
 * still open across a deploy holds an `index.html`/entry referencing the OLD
 * chunk hashes. When it lazy-loads a route it hasn't visited yet, the dynamic
 * `import()` requests a now-deleted hash; nginx's SPA `try_files` returns
 * `index.html` (text/html) for the missing `.js`, the browser refuses to
 * execute HTML as a module, and the import rejects with
 * "Failed to fetch dynamically imported module" / a module MIME error. With no
 * recovery the user hits the AppErrorBoundary ("Refreshing usually fixes it")
 * or a blank screen — and a manual hard reload is the only cure.
 *
 * Fix: detect that specific failure class and reload the page exactly ONCE so
 * the tab pulls the fresh `index.html` (with the new chunk hashes) and self-
 * heals. Critically this must NEVER loop: a *permanently* missing chunk must
 * reload once, then fall through to the normal error UI.
 *
 * Guard design:
 *   - A `sessionStorage` flag stores the timestamp of our last auto-reload.
 *   - `reloadOnceForChunkError()` reloads only if we have NOT reloaded within
 *     RELOAD_WINDOW_MS. So: first chunk error → set flag → reload; if the very
 *     next load fails the same way (chunk genuinely gone) → flag is fresh →
 *     we do NOT reload again → the error UI shows. No infinite loop.
 *   - `clearChunkReloadFlag()` is called when a lazy import SUCCEEDS, so once
 *     the tab is healthy a *later* deploy can trigger a fresh single reload.
 *     (We deliberately do NOT clear on raw app mount: after the reload the
 *     root shell mounts fine while the route chunk is still missing, so
 *     clearing on mount would re-arm the reload and loop.)
 *   - The RELOAD_WINDOW_MS timeout is a belt-and-braces backstop so the flag
 *     self-expires even if no successful import ever clears it.
 *
 * This is frontend-only and does NOT change bundling/hashing, register a
 * service worker, or touch nginx/deploy. The nginx `Cache-Control` fix for the
 * stale-`index.html` blank-screen case is a separate prod-ops step.
 */

import { lazy } from 'react';
import type { ComponentType } from 'react';

const FLAG = 'chunk-reload-once';
/** Don't auto-reload again within this window — prevents reload loops. */
const RELOAD_WINDOW_MS = 20_000;

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script|Loading chunk [\w-]+ failed|Loading CSS chunk/i;

/** True if `err` looks like a failed dynamic-import / chunk-load (vs a real app error). */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: unknown; message?: unknown };
  if (typeof e.name === 'string' && e.name === 'ChunkLoadError') return true;
  const msg =
    typeof e.message === 'string' ? e.message : typeof err === 'string' ? err : '';
  return CHUNK_ERROR_RE.test(msg);
}

function reloadedRecently(): boolean {
  try {
    const v = sessionStorage.getItem(FLAG);
    if (!v) return false;
    const ts = Number(v);
    return Number.isFinite(ts) && Date.now() - ts < RELOAD_WINDOW_MS;
  } catch {
    // No sessionStorage (private mode / blocked) → treat as "reloaded" so we
    // fail safe and never loop.
    return true;
  }
}

/**
 * Reload the page once to recover from a chunk-load failure. Returns true if a
 * reload was triggered, false if we already reloaded recently (caller should
 * then surface the normal error UI). Never reloads more than once per window.
 */
export function reloadOnceForChunkError(): boolean {
  if (reloadedRecently()) return false;
  try {
    sessionStorage.setItem(FLAG, String(Date.now()));
  } catch {
    return false; // can't record the attempt → don't risk a loop
  }
  window.location.reload();
  return true;
}

/** Clear the guard so a future (post-recovery) chunk error can reload once again. */
export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(FLAG);
  } catch {
    /* ignore */
  }
}

/**
 * Install global backstops for dynamic-import failures that don't flow through
 * {@link lazyWithReload} — primarily Vite's `vite:preloadError` (emitted by the
 * built preload helper) and unhandled chunk-load promise rejections.
 * Idempotent and safe to call once at app boot.
 */
export function installChunkReloadHandler(): void {
  if (typeof window === 'undefined') return;

  // Vite emits this on a failed dynamic import / module preload.
  // IMPORTANT: do NOT call event.preventDefault() here. Doing so makes Vite
  // RESOLVE the import to `undefined` instead of rejecting it; lazyWithReload's
  // success `.then` would then run and clear the reload guard immediately after
  // we set it, defeating the loop guard and causing a reload loop on a
  // permanently-missing chunk. Letting it reject normally keeps the guard
  // intact; the brief Suspense/boundary state before the reload is harmless.
  window.addEventListener('vite:preloadError', () => {
    reloadOnceForChunkError();
  });

  // Backstop: a chunk-load rejection that escaped to the global scope.
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) reloadOnceForChunkError();
  });
}

/**
 * Drop-in replacement for React.lazy that self-heals a failed chunk load.
 * On a chunk-load error it reloads once (if allowed) and holds the Suspense
 * fallback during the reload instead of throwing to the error boundary; a
 * successful load clears the reload guard.
 */
// Mirror React.lazy's own generic signature (ComponentType<any>) so any route
// component — regardless of its props type — is accepted, exactly as `lazy` was.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory()
      .then((mod) => {
        // Re-arm recovery for a future deploy — but ONLY on a genuinely valid
        // module. Guarding on `default` avoids clearing the guard if a module
        // ever resolves empty/undefined (which would otherwise race the reload).
        if (mod && (mod as { default?: unknown }).default !== undefined) {
          clearChunkReloadFlag();
        }
        return mod;
      })
      .catch((err: unknown) => {
        if (isChunkLoadError(err) && reloadOnceForChunkError()) {
          // Reload triggered: return a never-settling promise so React keeps
          // showing the Suspense fallback (not the error boundary) until the
          // page navigates away.
          return new Promise<{ default: T }>(() => {});
        }
        // Not a chunk error, or we've already reloaded once → let the nearest
        // error boundary handle it (the user sees the normal error UI).
        throw err;
      }),
  );
}
